import { Skeleton } from '@mui/material'
import React, { FC, useState } from 'react'
import clsx from 'clsx'

import InfiniteScrollDataTable from '../../../../../../components/common/tables/InfiniteScrollDataTable'
import { RankNumber } from '../../../components/RankNumber'
import { HorseTrainer } from '../../../../../../components/rider/HorseTrainer/HorseTrainer'
import ViewsLoader from '../../../../../../components/loader/ViewsLoader'
import { CustomScoringTotal } from '../CustomScoringTotal/CustomScoringTotal'
import { DefaultScoringTotal } from '../DefaultScoringTotal/DefaultScoringTotal'

import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { RegistrationFeesType } from '../../../../../../models/event-fees/event-fees.interface'
import { IRegistrationByDayEx } from '../../ManageClinicNOtherOperationsTab'
import { IManageOption } from '../../types/IManageOption'

import { generateName } from '../../../../../../helpers/helpers'
import { getRankColor } from '../../../../../../helpers/rank'
import { useEventRiderTeamMembers } from '../../../../../../hooks/useEventRiderTeamMembers'
import { getFloatPrice } from '../../../../../../helpers/price'
import { getRegistrationFee } from '../../data/operations-tab-utils'

import FirestoreService from '../../../../../../services/firestoreService'

import { CONST } from '../../../../../../const/const'
import { IMAGE_CONSTS } from '../../../../../../const/image-const'
import { HORSE_NAME_DEFAULT } from '../../../../../../models/registrations-by-day/registrationByDay.interface'

interface PrizeMoneyTableProps {
  eventId: string
  registrationsByDayByClass: IRegistrationByDayInterface[]
  registrationsByDay: IRegistrationByDayInterface[] | null
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  registrationFees: any
  selectedClass: IManageOption | null
  onSortClassScore: (value: boolean) => Promise<void>
  fee: RegistrationFeesType | null
  currency: string
}

export const PrizeMoneyTable: FC<PrizeMoneyTableProps> = ({
  registrationsByDayByClass,
  registrationsByDay,
  setRegistrationsByDay,
  registrationFees,
  eventId,
  selectedClass,
  onSortClassScore,
  fee,
  currency,
}) => {
  const { eventTeamMembers, eventTeamMembersLoading } = useEventRiderTeamMembers(eventId)

  const [loading, setLoading] = useState(false)

  const onChangeEarning = async (event: any, row: IRegistrationByDayEx) => {
    const newRow = {
      ...row,
      score: {
        ...row.score,
        earning: event.target.value,
      },
    }

    let registrationsByDay_ =
      registrationsByDay?.map((registration) => {
        if (registration.id === newRow.id) {
          return newRow
        } else {
          return registration
        }
      }) ?? []

    setRegistrationsByDay([...registrationsByDay_])

    await FirestoreService.updateItem(
      CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.REGISTRATION_BY_DAY.NAME,
      newRow.id,
      newRow
    )
  }

  const columnsPrizeMoney = [
    {
      name: (
        <div className="w-full text-gray-400 font-normal text-[0.875rem] flex items-center">
          <span className="whitespace-nowrap">Rank</span>
        </div>
      ),
      cell: (row: IRegistrationByDayEx, index: number) => (
        <div className="flex flex-col md:flex-row mb-3 md:mb-0 md:items-center w-full">
          {row.loading ? (
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <RankNumber rank={Number(row.score?.rank) || index + 1} />
          )}
        </div>
      ),
      width: '10%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
          <button
            className="whitespace-nowrap flex items-center gap-1 hover:opacity-70 transition-all"
            onClick={async () => {
              setLoading(true)
              await onSortClassScore(!fee?.isAsc)
              setLoading(false)
            }}
          >
            Rider & Score
            <img
              src="/assets/og_icons/arrow.svg"
              alt=""
              className={clsx(
                'w-2 invert-[0.7] sepia-[0.1] saturate-[0.05] hue-rotate-[5deg] brightness-[0.6] contrast-[1.2]',
                fee?.isAsc ? 'rotate-0' : 'rotate-180'
              )}
            />
          </button>
        </span>
      ),
      cell: (row: IRegistrationByDayEx) => (
        <div className="flex items-center gap-4">
          <span className="w-11 h-11 rounded-full block shrink-0 my-2 overflow-hidden">
            {row.loading ? (
              <Skeleton
                variant="circular"
                width={44}
                height={44}
                className="mb-2"
                style={{ backgroundColor: '#F1F3F8' }}
              />
            ) : (
              <img
                src={row.riderProfilePicture ?? IMAGE_CONSTS.PLACEHOLDERS.USER}
                alt="icons"
                className="object-cover w-full h-full rounded-full"
                onError={(e) =>
                  ((e.target as any).src = `https://ui-avatars.com/api/?name=${generateName(
                    row.riderName ?? ''
                  )}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                }
              />
            )}
          </span>

          {row.loading ? (
            <Skeleton
              variant="rounded"
              width={100}
              height={20}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <div className="flex flex-col">
              <span className="text-SeabiscuitDark200ThemeColor text-base">{row?.riderName}</span>
              <p className="text-SeabiscuitDark200ThemeColor/60 text-nr">
                {row.score?.sheet?.type === 'hunter-jumper' && row.score.judges?.[0] ? (
                  <CustomScoringTotal row={row} />
                ) : Number(row.score?.totalPoints) ? (
                  <DefaultScoringTotal row={row} />
                ) : (
                  row.score?.scoreStatus || 'No score'
                )}
              </p>
            </div>
          )}
        </div>
      ),
      width: '30%',
    },
    {
      name: (
        <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
          <span className="whitespace-nowrap">Horse & Trainer</span>
        </span>
      ),
      cell: (row: IRegistrationByDayEx) => {
        return (
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full block shrink-0 my-2 overflow-hidden">
              {row.loading ? (
                <Skeleton
                  variant="circular"
                  width={44}
                  height={44}
                  className="mb-2"
                  style={{ backgroundColor: '#F1F3F8' }}
                />
              ) : (
                <img
                  src={row.horseProfilePicture ?? IMAGE_CONSTS.PLACEHOLDERS.USER}
                  alt="icons"
                  className="object-cover w-full h-full rounded-full"
                  onError={(e) =>
                    ((e.target as any).src = `https://ui-avatars.com/api/?name=${generateName(
                      row.horseName ?? ''
                    )}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                  }
                />
              )}
            </span>

            {row.loading ? (
              <Skeleton
                variant="rounded"
                width={100}
                height={20}
                style={{ backgroundColor: '#F1F3F8' }}
              />
            ) : (
              <div className="flex flex-col">
                <span className="text-SeabiscuitDark200ThemeColor  text-base">
                  {row?.horseName ?? HORSE_NAME_DEFAULT}
                </span>
                <span className="text-SeabiscuitDark200ThemeColor/60 text-nr flex items-center">
                  <HorseTrainer
                    eventTeamMembers={eventTeamMembers}
                    eventTeamMembersLoading={eventTeamMembersLoading}
                    rider={row}
                  />
                </span>
              </div>
            )}
          </div>
        )
      },
      width: '30%',
    },
    {
      name: (
        <span className="text-gray-400  text-[0.875rem] font-normal flex items-center justify-center w-full">
          <span className="whitespace-nowrap">Earning %</span>
        </span>
      ),
      cell: (row: IRegistrationByDayEx, index: number) => (
        <div className="justify-center w-full flex">
          {row.loading ? (
            <Skeleton
              variant="rounded"
              width={80}
              height={20}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <input
              data-rank={(Number(row.score?.rank) || 1) - 1}
              type="number"
              className="p-1.5 rounded-full w-[60px] border-0 outline-0 text-base text-center hover:opacity-70 transition-all cursor-pointer placeholder:text-base text-black"
              style={{
                backgroundColor: `${getRankColor(Number(row.score?.rank) || index + 1)}20`,
              }}
              placeholder="0%"
              defaultValue={row.score?.earning ?? ''}
              onBlur={(event) => onChangeEarning(event, row)}
            />
          )}
        </div>
      ),
      width: '15%',
    },
    {
      name: (
        <span className="text-gray-400  text-[0.875rem] font-normal flex items-center justify-center w-full">
          <span className="whitespace-nowrap">Prize Money</span>
        </span>
      ),
      cell: (row: IRegistrationByDayEx, index: number) => {
        if (selectedClass?.parentId) {
        }
        const registrationFee = getRegistrationFee(
          registrationFees?.registrationFees,
          selectedClass?.value ?? '',
          selectedClass?.parentId
        )

        return (
          <div className="justify-center w-full flex">
            {row.loading ? (
              <Skeleton
                variant="rounded"
                width={80}
                height={20}
                style={{ backgroundColor: '#F1F3F8' }}
              />
            ) : registrationFee?.prizeMoney && row.score?.earning ? (
              <span
                className="p-2 rounded-full min-w-[100px] text-center text-white"
                style={{ background: getRankColor(Number(row.score?.rank) || index + 1) }}
              >
                {getFloatPrice(
                  `${currency}${(Number(registrationFee?.prizeMoney) * Number(row.score.earning)) / 100})`
                )}
              </span>
            ) : (
              <span
                className="p-2 rounded-full min-w-[100px] text-center text-white"
                style={{ background: getRankColor(Number(row.score?.rank) || index + 1) }}
              >
                {currency}0
              </span>
            )}
          </div>
        )
      },
      width: '15%',
    },
  ]

  return (
    <div className="relative">
      <InfiniteScrollDataTable
        hasMore={false}
        className="exhibitorListTable"
        columns={columnsPrizeMoney}
        data={registrationsByDayByClass}
      />
      {loading && (
        <div
          className={
            'absolute top-0 left-0 w-full h-full flex justify-center items-center bg-white/50'
          }
        >
          <ViewsLoader />
        </div>
      )}
    </div>
  )
}
